import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import './styles.css';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '600px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    margin: '20px',
    borderRadius: '8px',
    fontFamily: "Source Sans Pro",
  },
}));

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 24px 24px 24px',
  backgroundColor: '#FFFFFF',
});

const StyledDialogContent = styled(DialogContent)({
  padding: '0',
  overflowY: 'visible',
  overflowX: 'hidden',
});

const PropertyNameText = styled(Typography)({
  fontFamily: "Source Sans Pro",
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#01151D',
  marginTop: '4px',
});

const AssociateTransactionModal = ({ open, onClose, propertyName, transactions = [], from = 'property' }) => {
  const history = useHistory();

  // Separate transactions into transactions and pre-transactions
  const regularTransactions = transactions.filter(
    (tx) => tx.transactionCategory === 'Transaction'
  );

  const preTransactions = transactions.filter(
    (tx) => tx.transactionCategory === 'Pre-Transaction'
  );

  const getStatusClassName = (status) => {
    if (!status) return 'status-default';
    if (status === 1) return 'status-new';
    if (status === 2) return 'status-on-track';
    if (status === 5) return 'status-completed';
    if (status === 3) return 'status-delayed';
    if (status === 4) return 'status-cancelled';
    return 'status-default';
  };

  const handleTransactionClick = (transactionId, category) => {
    onClose();
    
    if (category === 'Transaction') {
      history.push(`/transaction/details/${transactionId}`, { from: from });
    } else if (category === 'Pre-Transaction') {
      history.push(`/pre-transaction/details/${transactionId}`, { from: from });
    }
     // Close modal after navigation
  };

  const renderTransactionItem = (transaction, isPreTransaction = false) => {
    // Support both transactionStatus and transactionStatusName
    const status = transaction.transactionStatusName;
    const statusClass = getStatusClassName(transaction.transactionStatusId);
    const iconLetter = isPreTransaction ? 'P' : 'T';
    
    return (
      <div 
        key={transaction.transactionId} 
        className={`transaction-item ${statusClass}`}
        onClick={() => handleTransactionClick(transaction.transactionId, transaction.transactionCategory)}
      >
        <div className="transaction-icon">
          <div className="icon-circle">{iconLetter}</div>
        </div>
        <div className="transaction-content">
          <div className="transaction-name">{transaction.transactionName}</div>
          <div className={`transaction-status ${statusClass}`}>
            {status}
          </div>
        </div>
      </div>
    );
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md">
      <StyledDialogTitle>
        <Box>
          <Typography
            sx={{
              fontFamily: "Source Sans Pro",
              fontSize: '20px',
              fontWeight: 400,
              lineHeight: '28px',
              color: '#01151D',
              margin: 0,
            }}
          >
            Associated transactions
          </Typography>
          <PropertyNameText>Property name: {propertyName}</PropertyNameText>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#01151D',
            padding: '4px',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#f3f4f6',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <div className="transactions-scroll-container">
          {regularTransactions.length > 0 && (
            <div className="transaction-section">
              <div className="section-header">TRANSACTIONS</div>
              <div className="transaction-list">
                {regularTransactions.map((tx) => renderTransactionItem(tx, false))}
              </div>
            </div>
          )}
          {preTransactions.length > 0 && (
            <div className="transaction-section">
              <div className="section-header">PRE-TRANSACTIONS</div>
              <div className="transaction-list">
                {preTransactions.map((tx) => renderTransactionItem(tx, true))}
              </div>
            </div>
          )}
          {transactions.length === 0 && (
            <div className="empty-message">No associated transactions</div>
          )}
        </div>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default AssociateTransactionModal;